import type {
  DemoPreview,
  DemoPreviewPainPoint,
  DemoValidatorScore,
  MockEvaluationResult,
  DemoValidationMessage,
} from "./types";

export interface MomTestAnswerMap {
  [key: string]: string;
}

function normalizeConsistencyScore(score?: number): number {
  if (score == null) {
    return 0;
  }

  const normalized = score > 1 ? score : score * 100;
  return Math.round(Math.max(0, Math.min(100, normalized)));
}

function deriveConfidence(
  consistency: number,
  painPointCount: number,
): DemoValidatorScore["confidence"] {
  if (consistency >= 78 && painPointCount >= 3) {
    return "high";
  }

  if (consistency >= 55 && painPointCount >= 2) {
    return "medium";
  }

  return "low";
}

function deriveSeverityIndex(painPoints: DemoPreviewPainPoint[]): number {
  if (painPoints.length === 0) {
    return 0;
  }

  const weighted = painPoints.reduce((total, painPoint) => {
    return total + painPoint.severityScore * Math.max(1, painPoint.frequency);
  }, 0);

  const frequencyTotal = painPoints.reduce(
    (total, painPoint) => total + Math.max(1, painPoint.frequency),
    0,
  );

  return Math.round(weighted / frequencyTotal);
}

export interface DeriveValidatorScoreOptions {
  momTestAnswers?: MomTestAnswerMap;
}

function momTestScoreAdjustment(momTestAnswers: MomTestAnswerMap): {
  consistencyDelta: number;
  confidenceBoost: 1 | 0 | -1;
} {
  const answeredCount = Object.values(momTestAnswers).filter(
    (v) => v.trim().length > 0,
  ).length;
  const totalQuestions = 5;
  const completionRatio = answeredCount / totalQuestions;

  if (completionRatio >= 0.8) {
    return { consistencyDelta: 5, confidenceBoost: 1 };
  }
  if (completionRatio >= 0.4) {
    return { consistencyDelta: 0, confidenceBoost: 0 };
  }
  return { consistencyDelta: -3, confidenceBoost: -1 };
}

export function deriveValidatorScore(
  preview: DemoPreview,
  options?: DeriveValidatorScoreOptions,
): DemoValidatorScore {
  const consistency = normalizeConsistencyScore(preview.consistencyScore);
  const severityIndex = deriveSeverityIndex(preview.painPoints);
  const confidence = deriveConfidence(consistency, preview.painPoints.length);

  const rationale: string[] = [
    `Consistency baseline from report metadata: ${consistency}%`,
    `Severity index derived from weighted pain-point intensity: ${severityIndex}/100`,
    `Confidence is ${confidence} based on ${preview.painPoints.length} clustered pain points`,
  ];

  if (preview.postsCollected != null) {
    rationale.push(
      `Signal volume includes ${preview.postsCollected.toLocaleString()} posts`,
    );
  }

  // Mom Test answer influence — adjustment table affects numeric score fields AND rationale
  const momTestAnswers = options?.momTestAnswers;
  if (momTestAnswers && Object.keys(momTestAnswers).length > 0) {
    const answeredCount = Object.values(momTestAnswers).filter(
      (v) => v.trim().length > 0,
    ).length;
    const totalQuestions = 5;

    const { consistencyDelta, confidenceBoost } =
      momTestScoreAdjustment(momTestAnswers);

    let adjustedConsistency = consistency;
    let adjustedConfidence: DemoValidatorScore["confidence"] = confidence;

    if (consistencyDelta !== 0) {
      adjustedConsistency = Math.max(
        0,
        Math.min(100, consistency + consistencyDelta),
      );
      rationale.push(
        `Mom Test: ${answeredCount}/${totalQuestions} answered — ${consistencyDelta > 0 ? "strong" : "limited"} signal, consistency ${consistencyDelta > 0 ? "+" : ""}${consistencyDelta}`,
      );
    } else {
      rationale.push(
        `Mom Test: ${answeredCount}/${totalQuestions} answered — partial validation applied`,
      );
    }

    if (confidenceBoost !== 0) {
      const confidenceOrder = { low: 0, medium: 1, high: 2 } as const;
      const currentOrder = confidenceOrder[adjustedConfidence];
      const newOrder = Math.max(
        0,
        Math.min(2, currentOrder + confidenceBoost),
      ) as 0 | 1 | 2;
      adjustedConfidence = (["low", "medium", "high"] as const)[newOrder];
      rationale.push(
        `Mom Test: confidence ${confidenceBoost > 0 ? "upgraded" : "downgraded"} based on ${answeredCount}/${totalQuestions} answers`,
      );
    }

    return {
      consistency: adjustedConsistency,
      confidence: adjustedConfidence,
      severityIndex,
      rationale,
    };
  }

  return {
    consistency,
    confidence,
    severityIndex,
    rationale,
  };
}

// ─── Validation score iteration — heuristic mock evaluation ─────────────────

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function countCoherentMomTestAnswers(
  momTestAnswers?: MomTestAnswerMap,
): number {
  if (!momTestAnswers) return 0;
  return Object.values(momTestAnswers).filter((answer) => {
    const trimmed = answer.trim();
    return trimmed.length >= 10; // minimum useful length
  }).length;
}

function countQuotes(preview: DemoPreview): number {
  return preview.painPoints.reduce((total, pp) => {
    return total + (pp.evidence?.length ?? 0);
  }, 0);
}

export interface MockLlamaEvaluationOptions {
  momTestAnswers?: MomTestAnswerMap;
}

/**
 * Demo-only heuristic evaluation — no real LLM calls.
 * Produces a 0-10 score and feedback messages based on Mom Test coherence,
 * ideal customer presence, pain-point count, and quote count.
 */
export function mockLlamaEvaluation(
  preview: DemoPreview,
  options?: MockLlamaEvaluationOptions,
): MockEvaluationResult {
  const momTestAnswers = options?.momTestAnswers;

  // Base score from consistency (0-100) -> (0-10)
  const baseConsistency =
    preview.validatorContext?.score.consistency ??
    null ??
    (preview.consistencyScore != null
      ? normalizeConsistencyScore(preview.consistencyScore)
      : null) ??
    0;

  const baseScore = clamp(Math.round(baseConsistency / 10), 0, 10);

  // Mom Test signal: +1 per coherent answer, min 0, max +5
  const coherentCount = countCoherentMomTestAnswers(momTestAnswers);
  const momTestBonus = clamp(coherentCount, 0, 5);

  // Empty / incoherent Mom Test: -2 flat
  // "Incoherent" means answered but not coherent; "empty" means all answers are blank/trim-empty
  const totalAnswered = momTestAnswers
    ? Object.values(momTestAnswers).filter((v) => v.trim().length > 0).length
    : 0;
  const emptyIncoherent = totalAnswered === 0 || coherentCount === 0 ? -2 : 0;

  // Ideal customer bonus — no penalty when absent, only a warning message.
  // The absence warning is sufficient; the score must reflect real signal only.
  const hasIdealCustomer = (preview.idealCustomers?.length ?? 0) > 0;
  const idealCustomerDelta = hasIdealCustomer ? 2 : 0;

  // Pain point count >= 3: +1
  const painPointBonus = preview.painPoints.length >= 3 ? 1 : 0;

  // Quote count >= 2: +1
  const quoteCount = countQuotes(preview);
  const quoteBonus = quoteCount >= 2 ? 1 : 0;

  // Sum and clamp
  const rawScore =
    baseScore +
    momTestBonus +
    emptyIncoherent +
    idealCustomerDelta +
    painPointBonus +
    quoteBonus;
  const score = clamp(rawScore, 0, 10);

  // Tone derivation
  let tone: MockEvaluationResult["tone"] = "neutral";
  if (score >= 8) tone = "encouraging";
  else if (score >= 5) tone = "neutral";
  else if (score >= 3) tone = "concerned";
  else tone = "critical";

  // Messages
  const messages: DemoValidationMessage[] = [];

  if (totalAnswered === 0 || coherentCount === 0) {
    messages.push({
      type: "warning",
      text: "Mom Test answers are vague or missing — this weakens validation confidence",
    });
  }

  if (!hasIdealCustomer) {
    messages.push({
      type: "warning",
      text: "No ideal customer profile defined — Lorem AI cannot target messaging effectively",
    });
  }

  if (preview.painPoints.length < 3) {
    messages.push({
      type: "suggestion",
      text: "Add more pain points to strengthen evidence base",
    });
  }

  if (quoteCount < 2) {
    messages.push({
      type: "suggestion",
      text: "Capture customer quotes to ground insights in real language",
    });
  }

  if (baseScore < 5) {
    messages.push({
      type: "suggestion",
      text: "Consistency is low — consider aligning insights across sources",
    });
  }

  // Strong signal: no or one positive message
  if (messages.length === 0) {
    messages.push({
      type: "positive",
      text: "Validation signals are strong — ready for insight generation",
    });
  }

  return { score, tone, messages };
}
